import { shortName } from "./survey";
import type {
  ExternalMetric,
  ExternalRow,
  Office,
  Race,
  Side,
  StateInfo,
} from "./types";

/* ── Pasted forecasts ──────────────────────────────
 * Turns text copied from a forecaster (Silver Bulletin tables, a Google
 * Sheet, a Substack post, Cook ratings…) into per-race rows. One race per
 * line; the parser finds the state, the office and the numbers, and uses
 * candidate names or party markers to tell which side a number belongs to.
 * Lines where the side had to be assumed are counted so the preview can
 * flag them.
 */

export interface ParseOptions {
  /** Office used when a line doesn't name one. */
  defaultOffice: Office;
  metric: ExternalMetric;
  races: Race[];
  states: StateInfo[];
}

export interface ParseResult {
  /** Lines where a number's side was assumed (first number = Democrat). */
  assumed: number;
  rows: ExternalRow[];
  /** Lines with numbers that could not be matched to a race. */
  skipped: string[];
}

interface Token {
  /** Party letter attached to the number, e.g. the "D" of "D+3". */
  letter: string | null;
  signed: boolean;
  start: number;
  value: number;
}

/* ── Constants ─────────────────────────────────── */

const OFFICE_HINTS: [RegExp, Office][] = [
  [/\b(gov|governor|governors|gubernatorial)\b/i, "governor"],
  [/\b(sen|senate)\b/i, "senate"],
  [/\b(house|generic|congress|congressional)\b/i, "house"],
];

const NATIONAL = /\b(national|nationwide|generic|overall)\b/i;

const RATING =
  /\b(safe|solid|likely|lean|tilt)\s*(d|r|dem|democrat|democratic|rep|republican|gop)\b|\btoss[\s-]?up\b/i;

const RATING_ODDS: Record<string, number> = {
  lean: 0.7,
  likely: 0.88,
  safe: 0.98,
  solid: 0.98,
  tilt: 0.58,
};

const LEFT_WORDS = /(\bdem(s|ocrat|ocrats|ocratic)?\b|\(d\)|\bd\b)/i;
const RIGHT_WORDS = /(\brep(ublican|ublicans)?\b|\bgop\b|\(r\)|\br\b)/i;
const INDEPENDENT_WORDS = /(\bind(ependent)?\b|\(i\))/i;

const TOKEN =
  /\b([DRI])\s*\+\s*(\d+(?:\.\d+)?)|(?<=^|[\s(|,;:])([+\-−])\s*(\d+(?:\.\d+)?)|(\d+(?:\.\d+)?)\s*%?/g;

/* ── Helpers ───────────────────────────────────── */

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const detectOffice = (line: string): Office | null =>
  OFFICE_HINTS.find(([pattern]) => pattern.test(line))?.[1] ?? null;

const detectState = (line: string, states: StateInfo[]): string | null => {
  const byName = [...states]
    .sort((a, b) => b.name.length - a.name.length)
    .find((s) =>
      new RegExp(
        `\\b${escapeRegExp(s.name).replace(/ /g, "\\s+")}\\b`,
        "i"
      ).test(line)
    );
  if (byName) return byName.abbr;
  const abbrs = new Set(states.map((s) => s.abbr));
  for (const match of line.matchAll(/\b([A-Z]{2})\b/g)) {
    if (abbrs.has(match[1])) return match[1];
  }
  return null;
};

const letterSide = (letter: string, race: Race | null): Side | null => {
  if (letter === "R") return "right";
  if (letter === "D") return race?.left?.party === "I" ? null : "left";
  return race?.left?.party === "I" ? "left" : null;
};

/** Side named in the text right before a number (candidate or party). */
const contextSide = (context: string, race: Race | null): Side | null => {
  const text = context.toLowerCase();
  const leftAt = race?.left
    ? text.lastIndexOf(shortName(race.left).toLowerCase())
    : -1;
  const rightAt = race?.right
    ? text.lastIndexOf(shortName(race.right).toLowerCase())
    : -1;
  if (leftAt >= 0 || rightAt >= 0) return leftAt > rightAt ? "left" : "right";
  if (race?.left?.party === "I" && INDEPENDENT_WORDS.test(context))
    return "left";
  if (RIGHT_WORDS.test(context)) return "right";
  if (LEFT_WORDS.test(context) && race?.left?.party !== "I") return "left";
  return null;
};

const tokenize = (line: string): Token[] => {
  const tokens: Token[] = [];
  for (const match of line.matchAll(TOKEN)) {
    const start = match.index ?? 0;
    if (match[1] && match[2]) {
      tokens.push({
        letter: match[1],
        signed: true,
        start,
        value: Number(match[2]),
      });
    } else if (match[3] && match[4]) {
      const sign = match[3] === "+" ? 1 : -1;
      tokens.push({
        letter: null,
        signed: true,
        start,
        value: sign * Number(match[4]),
      });
    } else if (match[5]) {
      const value = Number(match[5]);
      // Years, district numbers and counts aren't forecasts.
      if (value <= 100)
        tokens.push({ letter: null, signed: false, start, value });
    }
  }
  return tokens;
};

/** Per-line format when auto-detecting: margins, then percentages (odds),
    then ratings; bare numbers are read as odds. */
const detectMetric = (line: string): Exclude<ExternalMetric, "auto"> => {
  if (/\b[DRI]\s*\+\s*\d/.test(line) || /(^|\s)[+\-−]\d/.test(line)) {
    return "margin";
  }
  if (/\d\s*%/.test(line)) return "probability";
  if (RATING.test(line)) return "rating";
  return "probability";
};

/* ── Parser ────────────────────────────────────── */

export const parseExternal = (
  text: string,
  options: ParseOptions
): ParseResult => {
  const rows = new Map<string, ExternalRow>();
  const skipped: string[] = [];
  let assumed = 0;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.replace(/\t/g, " | ").trim();
    if (!line) continue;

    const office = detectOffice(line) ?? options.defaultOffice;
    const state =
      detectState(line, options.states) ??
      (office === "house" && NATIONAL.test(line) ? "US" : null);
    const metric =
      options.metric === "auto" ? detectMetric(line) : options.metric;
    const rating = metric === "rating" ? RATING.exec(line) : null;
    const tokens = metric === "rating" ? [] : tokenize(line);
    if (!rating && tokens.length === 0) continue;
    if (!state) {
      skipped.push(rawLine.trim());
      continue;
    }

    const race =
      office === "house"
        ? null
        : (options.races.find(
            (r) => r.office === office && r.state === state
          ) ?? null);
    const sided = tokens.map((token, i) => {
      const from = i > 0 ? tokens[i - 1].start : 0;
      const side = token.letter
        ? letterSide(token.letter, race)
        : contextSide(line.slice(from, token.start), race);
      return { ...token, side };
    });

    let leftProbability: number | null = null;
    let margin: number | null = null;
    let guessed = false;

    if (rating) {
      if (/toss/i.test(rating[0])) leftProbability = 0.5;
      else {
        const odds = RATING_ODDS[rating[1].toLowerCase()] ?? 0.5;
        const right = /^(r|rep|republican|gop)$/i.test(rating[2]);
        leftProbability = right ? 1 - odds : odds;
      }
    } else if (metric === "margin") {
      const pick = sided.find((t) => t.signed) ?? null;
      if (pick) {
        const magnitude = Math.abs(pick.value);
        if (pick.side === "right") margin = -magnitude;
        else if (pick.side === "left") margin = magnitude;
        else {
          margin = pick.value;
          guessed = true;
        }
      }
    } else {
      const values = sided.filter((t) => !t.signed);
      const scale = values.some((t) => t.value > 1) ? 100 : 1;
      const left = values.find((t) => t.side === "left");
      const right = values.find((t) => t.side === "right");
      let l = left ? left.value / scale : null;
      let r = right ? right.value / scale : null;
      if (l === null && r === null && values.length > 0) {
        l = values[0].value / scale;
        r = values.length > 1 ? values[1].value / scale : null;
        guessed = true;
      }
      if (metric === "probability") {
        if (l !== null && r !== null && l + r > 0)
          leftProbability = l / (l + r);
        else if (l !== null) leftProbability = l;
        else if (r !== null) leftProbability = 1 - r;
      } else if (l !== null && r !== null) {
        margin = (l - r) * 100;
      }
    }

    if (leftProbability === null && margin === null) {
      skipped.push(rawLine.trim());
      continue;
    }
    if (guessed) assumed++;
    rows.set(`${office}:${state}`, {
      leftProbability:
        leftProbability === null
          ? null
          : Math.min(1, Math.max(0, leftProbability)),
      margin,
      office,
      state,
    });
  }

  return { assumed, rows: [...rows.values()], skipped };
};
